import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Banner,
  Card,
  Container,
  HStack,
  Heading,
  IconButton,
  Spinner,
  Tabs,
  Text,
  VStack,
} from 'wss3-forge'
import { Dismiss16Regular } from '@fluentui/react-icons'
import { cancelTask, listTasks, type Task } from '../lib/api.js'

const TAB_ORDER = ['active', 'done', 'failed'] as const
type TasksTab = (typeof TAB_ORDER)[number]

const TAB_LABEL: Record<TasksTab, string> = {
  active: 'En cours',
  done: 'Terminées',
  failed: 'Échouées',
}

const STATUS_VARIANT: Record<Task['status'], 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'info',
  running: 'info',
  done: 'success',
  failed: 'error',
  cancelled: 'default',
}

const STATUS_LABEL: Record<Task['status'], string> = {
  pending: 'En attente',
  running: 'En cours',
  done: 'Terminée',
  failed: 'Échec',
  cancelled: 'Annulée',
}

function formatDue(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const diff = date.getTime() - Date.now()
  const abs = Math.abs(diff)
  const future = diff > 0
  if (abs < 60_000) return future ? 'dans un instant' : 'à l’instant'
  if (abs < 3_600_000) {
    const m = Math.round(abs / 60_000)
    return future ? `dans ${m} min` : `il y a ${m} min`
  }
  if (abs < 86_400_000) {
    const h = Math.round(abs / 3_600_000)
    return future ? `dans ${h} h` : `il y a ${h} h`
  }
  return date.toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function isInTab(t: Task, tab: TasksTab): boolean {
  if (tab === 'active') return t.status === 'pending' || t.status === 'running'
  if (tab === 'done') return t.status === 'done'
  return t.status === 'failed' || t.status === 'cancelled'
}

export function TasksPage() {
  const [tab, setTab] = useState<TasksTab>('active')
  const qc = useQueryClient()

  // Fetch all then filter client-side: keeps the count-per-tab live without
  // running 3 queries in parallel.
  const tasksQuery = useQuery<Task[]>({
    queryKey: ['tasks', 'all'],
    queryFn: () => listTasks(),
    refetchInterval: 10_000,
  })

  const cancel = useMutation({
    mutationFn: (id: string) => cancelTask(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  })

  const all = tasksQuery.data ?? []

  const counts = useMemo(() => {
    const c: Record<TasksTab, number> = { active: 0, done: 0, failed: 0 }
    for (const t of all) {
      if (isInTab(t, 'active')) c.active += 1
      else if (isInTab(t, 'done')) c.done += 1
      else c.failed += 1
    }
    return c
  }, [all])

  const tasks = useMemo(() => all.filter((t) => isInTab(t, tab)), [all, tab])
  const empty = !tasksQuery.isLoading && tasks.length === 0

  return (
    <Container maxWidth="lg" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Tâches</Heading>
          <Text color="muted">Rappels, jobs en cours, historique.</Text>
        </VStack>

        <Tabs
          tabs={TAB_ORDER.map((id) => ({ id, label: TAB_LABEL[id], count: counts[id] }))}
          value={tab}
          onChange={(id) => setTab(id as TasksTab)}
          variant="underline"
        />

        {tasksQuery.error && (
          <Banner variant="error" title="Échec du chargement">
            {String((tasksQuery.error as Error).message)}
          </Banner>
        )}

        {tasksQuery.isLoading && (
          <HStack gap="sm" align="center">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Chargement…</Text>
          </HStack>
        )}

        {empty && (
          <Banner variant="info">Aucune tâche dans cette catégorie.</Banner>
        )}

        <VStack gap="sm" fullWidth>
          {tasks.map((t) => (
            <Card key={t.id} padding="md">
              <VStack gap="xs" fullWidth>
                <HStack justify="between" align="start" gap="sm" fullWidth>
                  <HStack gap="xs" align="center">
                    <Badge variant={STATUS_VARIANT[t.status]}>{STATUS_LABEL[t.status]}</Badge>
                    <Text size="xs" color="muted">
                      {t.type}
                      {t.due_at ? ` · ${formatDue(t.due_at)}` : ''}
                      {t.attempts > 0 ? ` · ${t.attempts} essai(s)` : ''}
                    </Text>
                  </HStack>
                  {(t.status === 'pending' || t.status === 'running') && (
                    <IconButton
                      icon={<Dismiss16Regular />}
                      tooltip="Annuler"
                      aria-label={`Annuler la tâche ${t.id.slice(0, 8)}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => cancel.mutate(t.id)}
                      disabled={cancel.isPending}
                    />
                  )}
                </HStack>
                <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{t.description}</Text>
                {t.last_error && (
                  <Text size="xs" color="muted" style={{ fontStyle: 'italic' }}>
                    Erreur : {t.last_error}
                  </Text>
                )}
              </VStack>
            </Card>
          ))}
        </VStack>
      </VStack>
    </Container>
  )
}
